import {useEffect, useMemo, useState} from 'react'
import {useI18n} from '@/i18n'
import {useDialog} from '@/hooks/useDialog'
import {ApiError} from '@/api/client'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {ErrorBanner} from '@/components/ui/error-banner'
import {ExpressionEditor} from '@/components/ui/expression-editor'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {CardSection} from '@/components/shared/CardSection'
import {SCHEMA_TYPES_CERTIFICATION} from '@/types/expression'
import {useCertificationCelEngine} from '@/lib/cel/use-cel-engine'
import {ScopeMode, type ScopeMode as ScopeModeType} from '@/features/certification/scope-mode'
import {ConditionsEditor} from '@/features/certification/components/ConditionsEditor'
import {ScopePreviewPanel} from '@/features/certification/components/ScopePreviewPanel'
import {useCertificationScopePreview} from '@/features/certification/hooks/useCertificationScopePreview'
import type {ScopeConditionFormEntry} from '@/features/certification/hooks/useCertificationPolicyWizardForm'

export interface WizardStepScopeProps {
    scopeMode: ScopeModeType
    conditions: ScopeConditionFormEntry[]
    conditionExpr: string
    onScopeModeChange: (mode: ScopeModeType) => void
    onConditionsChange: (conditions: ScopeConditionFormEntry[]) => void
    onConditionExprChange: (expr: string) => void
    /**
     * Lifts the runtime SCOPE gate to the wizard shell: `true` while a preview
     * debounce/request is in flight OR a server CEL 400 is unresolved — the shell
     * AND-combines this with `isStepValid(SCOPE)` to gate Next (spec §5.2, §7.3).
     */
    onPreviewGateChange?: (blockNext: boolean) => void
    disabled?: boolean
}

/**
 * SCOPE step: conditions ⟷ expression sub-mode toggle (destructive-switch
 * confirmation when the cleared field is non-empty), structured `ConditionsEditor`
 * or CEL `ExpressionEditor` with an inline server-CEL `ErrorBanner`, and an
 * always-visible `ScopePreviewPanel`. The `beforeunload` guard (FE-43) is active
 * while a typed-but-unsubmitted expression exists on this step.
 */
export function WizardStepScope({
    scopeMode,
    conditions,
    conditionExpr,
    onScopeModeChange,
    onConditionsChange,
    onConditionExprChange,
    onPreviewGateChange,
    disabled = false,
}: WizardStepScopeProps) {
    const i18n = useI18n()
    const certificationEngine = useCertificationCelEngine()
    const switchModeDialog = useDialog()
    const [pendingScopeMode, setPendingScopeMode] = useState<ScopeModeType | null>(null)
    const [serverCelError, setServerCelError] = useState<Error | null>(null)

    const preview = useCertificationScopePreview({scopeMode, conditions, conditionExpr})

    // WHY: surface a server CEL 400 inline below the editor (not the global toast).
    // Any other preview error is non-blocking and handled by the panel's error state.
    useEffect(() => {
        if (scopeMode !== ScopeMode.EXPRESSION) {
            setServerCelError(null)
            return
        }
        const err = preview.error
        if (err instanceof ApiError && err.status === 400) {
            setServerCelError(new Error(`${i18n.certificationPolicies.expressionInvalid}: ${err.message}`))
        }
    }, [preview.error, scopeMode, i18n.certificationPolicies.expressionInvalid])

    const blockNext = preview.inFlight || serverCelError !== null
    useEffect(() => {
        onPreviewGateChange?.(blockNext)
    }, [blockNext, onPreviewGateChange])

    // WHY: unsaved-changes guard (FE-43) — active only while on SCOPE with a
    // typed-but-unsubmitted expression. Cleared once the user advances past SCOPE
    // (this component unmounts → effect cleanup removes the listener).
    const expressionDirty = scopeMode === ScopeMode.EXPRESSION && conditionExpr.trim().length > 0
    useEffect(() => {
        if (!expressionDirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [expressionDirty])

    const applyScopeMode = (mode: ScopeModeType) => {
        setServerCelError(null)
        if (mode === ScopeMode.EXPRESSION) {
            onConditionsChange([])
        } else {
            onConditionExprChange('')
        }
        onScopeModeChange(mode)
    }

    const requestScopeMode = (mode: ScopeModeType) => {
        if (mode === scopeMode) return
        const losingData = mode === ScopeMode.EXPRESSION
            ? conditions.length > 0
            : conditionExpr.trim().length > 0
        if (losingData) {
            setPendingScopeMode(mode)
            switchModeDialog.open()
            return
        }
        applyScopeMode(mode)
    }

    const confirmSwitchMode = () => {
        if (pendingScopeMode) applyScopeMode(pendingScopeMode)
        setPendingScopeMode(null)
        switchModeDialog.close()
    }

    const cancelSwitchMode = () => {
        setPendingScopeMode(null)
        switchModeDialog.close()
    }

    const handleExprChange = (value: string) => {
        setServerCelError(null)
        onConditionExprChange(value)
    }

    const switchConfirmBody = useMemo(() => {
        if (pendingScopeMode === ScopeMode.EXPRESSION) {
            return i18n.certificationPolicies.wizard.switchToExpressionConfirmBody({count: conditions.length})
        }
        return i18n.certificationPolicies.wizard.switchToConditionsConfirmBody
    }, [pendingScopeMode, conditions.length, i18n])

    return (
        <CardSection
            title={i18n.certificationPolicies.wizard.stepScope}
            data-testid="scope-card"
        >
            <Stack gap="md">
                <InlineRow gap="xs" data-testid="scope-mode-toggle">
                    <Button
                        type="button"
                        variant={scopeMode === ScopeMode.CONDITIONS ? 'default' : 'outline'}
                        size="sm"
                        disabled={disabled}
                        onClick={() => requestScopeMode(ScopeMode.CONDITIONS)}
                        data-testid="btn-scope-mode-conditions"
                    >
                        {i18n.certificationPolicies.wizard.scopeModeConditions}
                    </Button>
                    <Button
                        type="button"
                        variant={scopeMode === ScopeMode.EXPRESSION ? 'default' : 'outline'}
                        size="sm"
                        disabled={disabled}
                        onClick={() => requestScopeMode(ScopeMode.EXPRESSION)}
                        data-testid="btn-scope-mode-expression"
                    >
                        {i18n.certificationPolicies.wizard.scopeModeExpression}
                    </Button>
                </InlineRow>

                {scopeMode === ScopeMode.EXPRESSION ? (
                    <Stack gap="sm">
                        <Label data-testid="scope-expression-label">{i18n.certificationPolicies.expressionLabel}</Label>
                        <ExpressionEditor
                            value={conditionExpr}
                            onChange={handleExprChange}
                            placeholder={i18n.certificationPolicies.expressionPlaceholder as string}
                            rows={6}
                            schemaTypes={SCHEMA_TYPES_CERTIFICATION}
                            getEngine={() => certificationEngine}
                            data-testid="expression-editor"
                        />
                        {serverCelError && (
                            <div data-testid="expression-error-banner">
                                <ErrorBanner error={serverCelError}/>
                            </div>
                        )}
                    </Stack>
                ) : (
                    <Stack gap="xs">
                        <Label data-testid="scope-conditions-label">{i18n.certificationPolicies.wizard.scopeModeConditions}</Label>
                        <ConditionsEditor
                            conditions={conditions}
                            onChange={onConditionsChange}
                            disabled={disabled}
                        />
                    </Stack>
                )}

                <CardSection
                    title={i18n.certificationPolicies.wizard.previewSectionTitle}
                    data-testid="preview-section"
                >
                    <ScopePreviewPanel
                        data={preview.data}
                        error={serverCelError ? null : preview.error}
                        inFlight={preview.inFlight}
                        idle={preview.idle}
                        onRetry={preview.retry}
                    />
                </CardSection>
            </Stack>

            <Dialog open={switchModeDialog.isOpen} onOpenChange={switchModeDialog.bind.onOpenChange}>
                <DialogContent data-testid="dialog-switch-mode">
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationPolicies.wizard.switchModeConfirmTitle}</DialogTitle>
                        <DialogDescription>{switchConfirmBody}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="switch-mode"
                        onCancel={cancelSwitchMode}
                        saving={false}
                        saveLabel={i18n.common.confirm}
                        saveType="button"
                        onSave={confirmSwitchMode}
                    />
                </DialogContent>
            </Dialog>
        </CardSection>
    )
}
